/**
 * Client-side mapping for the `supplierProductionSchedule` callable.
 *
 * Supplier accounts cannot read `timberProjects` / `projectTasks` /
 * `taskGroups` directly (staff-only Firestore rules), so their Project Hub
 * data arrives through the callable and gets mapped into the same shapes the
 * CRM context serves to staff.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import type {
  EnhancedDeal,
  FrameType,
  ProjectPhase,
  ProjectStatus,
  ProjectTask,
  TaskGroup,
  TaskStatus,
  TimberProject,
} from '../types';

export type SupplierScheduleResponse = {
  projects: Array<{
    id: string;
    dealId: string;
    name: string;
    location?: string;
    clientName?: string;
    status?: string;
    phase?: string;
    startDate?: string;
    targetEndDate?: string;
    contractedAt?: string;
    projectNumber?: string;
    descriptor?: string;
    dealTitle?: string;
    clientLabel?: string;
  }>;
  groups: Array<{ id: string; projectId: string; name: string; color: string; position: number }>;
  tasks: Array<{
    id: string;
    projectId: string;
    groupId: string;
    parentTaskId?: string;
    title: string;
    department: string;
    status: string;
    priority: string;
    progress: number;
    position: number;
    dependencies: string[];
    startDate: string;
    endDate: string;
    supplierOrgIds?: string[];
    supplierManual?: boolean;
    structureId?: string;
    structureName?: string;
    structureIds?: string[];
    structureNames?: string[];
  }>;
};

export type SupplierScheduleData = {
  projects: TimberProject[];
  groups: TaskGroup[];
  tasks: ProjectTask[];
  /**
   * Minimal deal rows (naming triple only) so hub lists can render
   * `#0053 Barn — client Smith` for supplier accounts that cannot read deals.
   */
  deals: EnhancedDeal[];
};

const PROJECT_STATUSES: ReadonlySet<string> = new Set([
  'planning', 'design', 'production', 'installation', 'completed', 'on-hold',
]);
const PROJECT_PHASES: ReadonlySet<string> = new Set([
  'estimating', 'preconstruction', 'contracted',
]);

function isoToDate(value: string | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function mapSupplierScheduleResponse(data: SupplierScheduleResponse): SupplierScheduleData {
  const projects = (data.projects ?? []).map(p => {
    const status: ProjectStatus = PROJECT_STATUSES.has(p.status ?? '')
      ? (p.status as ProjectStatus)
      : 'production';
    const phase: ProjectPhase = PROJECT_PHASES.has(p.phase ?? '')
      ? (p.phase as ProjectPhase)
      : 'contracted';
    return {
      id: p.id,
      dealId: p.dealId,
      name: p.name,
      location: p.location ?? '',
      clientEmail: '',
      clientName: p.clientName ?? p.clientLabel ?? '',
      status,
      phase,
      frameType: 'custom' as FrameType,
      startDate: isoToDate(p.startDate) ?? new Date(),
      targetEndDate: isoToDate(p.targetEndDate) ?? new Date(),
      contractedAt: isoToDate(p.contractedAt) ?? undefined,
      value: 0,
      projectType: '',
      createdAt: new Date(),
      updatedAt: new Date(),
    } as TimberProject;
  });

  const groups: TaskGroup[] = (data.groups ?? []).map(g => ({
    id: g.id,
    projectId: g.projectId,
    name: g.name,
    color: g.color,
    position: g.position,
  }));

  const tasks = (data.tasks ?? []).map(
    t =>
      ({
        id: t.id,
        projectId: t.projectId,
        groupId: t.groupId,
        parentTaskId: t.parentTaskId,
        title: t.title,
        department: t.department,
        assignedTo: [],
        status: t.status as TaskStatus,
        priority: t.priority,
        startDate: new Date(t.startDate),
        endDate: new Date(t.endDate),
        dependencies: t.dependencies ?? [],
        progress: t.progress ?? 0,
        position: t.position ?? 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        ...(t.supplierOrgIds?.length ? { supplierOrgIds: t.supplierOrgIds } : {}),
        ...(t.supplierManual ? { supplierManual: true } : {}),
        ...(t.structureId ? { structureId: t.structureId } : {}),
        ...(t.structureName ? { structureName: t.structureName } : {}),
        ...(t.structureIds?.length ? { structureIds: t.structureIds } : {}),
        ...(t.structureNames?.length ? { structureNames: t.structureNames } : {}),
      }) as ProjectTask,
  );

  const seenDealIds = new Set<string>();
  const deals: EnhancedDeal[] = [];
  for (const p of data.projects ?? []) {
    const dealId = (p.dealId || '').trim();
    if (!dealId || seenDealIds.has(dealId)) continue;
    if (!p.projectNumber && !p.descriptor && !p.dealTitle && !p.clientLabel) continue;
    seenDealIds.add(dealId);
    deals.push({
      id: dealId,
      title: p.dealTitle ?? '',
      projectNumber: p.projectNumber,
      descriptor: p.descriptor,
      clientLabel: p.clientLabel,
      contactName: p.clientLabel ?? '',
      // 'won' keeps these label-only rows out of active-deal pipelines.
      outcome: 'won',
    } as unknown as EnhancedDeal);
  }

  return { projects, groups, tasks, deals };
}

export async function fetchSupplierProductionSchedule(): Promise<SupplierScheduleData> {
  const fn = httpsCallable<Record<string, never>, SupplierScheduleResponse>(
    getFunctions(undefined, 'us-central1'),
    'supplierProductionSchedule',
  );
  const res = await fn({});
  return mapSupplierScheduleResponse(res.data);
}
